import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback, useEffect, Suspense } from "react";
import {
  ArrowLeft,
  Palette,
  Settings,
  Plus,
  Copy,
  Pencil,
  Trash2,
  ClipboardList,
  Table2,
  Eye,
  EyeOff,
  MoreHorizontal,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import {
  usePaginatedData,
  type PaginatedResponse,
  type ServerSortState,
} from "@/hooks/usePaginatedData";
import { EditTablesDialog } from "@/components/portal/tables/EditTablesDialog";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useMyAppBatchPermissions } from "@/hooks/useMyAppBatchPermissions";
import { useTablePrefetch } from "@/hooks/useTablePrefetch";
import { myappApi } from "@/services/api/myapp";
import type { AppDetail, Form } from "@/types/myapp";

export const Route = createFileRoute("/portal/apps/$appSlug/")({
  component: AppLandingPage,
});

function AppLandingPage() {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading app...</div>
        </div>
      }
    >
      <AppLandingPageContent />
    </Suspense>
  );
}

const TABLE_TYPE_LABELS: Record<string, string> = {
  simple: "Simple",
  advanced: "Advanced",
  latest: "Latest Only",
  versioned: "Versioned",
};

function AppLandingPageContent() {
  const { appSlug } = Route.useParams();
  const { token } = useAuth();
  const { can, isAdmin, isPlatformAdmin } = usePermissions();
  // Tier 2 RBAC gates. Object-level perms (tablePerms below) gate the
  // per-table actions; these RBAC checks layer on top, matching the
  // Tables area (myapp/tables.tsx).
  // Creating a table is org admin and above only (MyApp enforces it too).
  // can("create","tables") alone is true for org_member: CP grants members
  // create on the whole MyApp gateway so they can create records.
  const canCreateTables = can("create", "tables") && (isAdmin || isPlatformAdmin);
  const canStructure = can("edit", "tables");
  const canHide = can("edit", "tables");
  // Phase C hover-prefetch. Returns a no-op when the Phase B cache
  // flag is off (nothing reads the warmed slot) or on touch-only
  // devices (no hover signal).
  const prefetchTable = useTablePrefetch();
  const [app, setApp] = useState<AppDetail | null>(null);
  const [loadingApp, setLoadingApp] = useState(true);
  const [appError, setAppError] = useState<string | null>(null);

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [showHidden, setShowHidden] = useState(false);

  // Edit tables dialog
  const [editTablesOpen, setEditTablesOpen] = useState(false);

  // Clone dialog
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [tableToClone, setTableToClone] = useState<Form | null>(null);
  const [cloneName, setCloneName] = useState("");
  const [cloning, setCloning] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<Form | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [checkingDeps, setCheckingDeps] = useState(false);
  const [tableDeps, setTableDeps] = useState<{
    can_delete: boolean;
    dependencies: { table_name: string; field_name: string; table_id: string }[];
    record_count: number | null;
  } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch app data (header info)
  const fetchApp = useCallback(async () => {
    try {
      setLoadingApp(true);
      // Support both UUID (from app list) and slug (from bookmarks/direct links)
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(appSlug);
      const appData = isUuid
        ? await myappApi.apps.get(token, appSlug)
        : await myappApi.apps.getBySlug(token, appSlug);
      setApp(appData);
      setAppError(null);
    } catch (err) {
      setAppError(err instanceof Error ? err.message : "App not found");
    } finally {
      setLoadingApp(false);
    }
  }, [appSlug, token]);

  // Fetch app on mount
  useEffect(() => {
    fetchApp();
  }, [fetchApp]);

  // Server-side paginated tables fetch — sorting is a server param so it
  // orders the whole dataset, not just the fetched page.
  const fetchTables = useCallback(
    async (
      page: number,
      pageSize: number,
      sort?: ServerSortState,
    ): Promise<PaginatedResponse<Form>> => {
      if (!token || !app) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      const response = await myappApi.apps.getTables(token, app.id, {
        limit: pageSize,
        offset: (page - 1) * pageSize,
        search: searchTerm || undefined,
        showHidden: showHidden || undefined,
        sortBy: sort?.key as
          | "name"
          | "table_type"
          | "is_published"
          | "created_at"
          | undefined,
        sortOrder: sort?.direction,
      });
      return {
        data: response.items,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, app, searchTerm, showHidden],
  );

  const {
    data: tables,
    isLoading,
    error: tablesError,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
    requestSort,
    getSortDirection,
  } = usePaginatedData<Form>({
    fetchFn: fetchTables,
    initialPageSize: 25,
    dependencies: [token, app, searchTerm, showHidden],
    initialSort: { key: "name", direction: "asc" },
  });

  const displayTables = tables;

  // Batch object-level permissions — one call for all visible tables.
  // Used to gate the per-table name link and 3-dots actions by the
  // viewer's role (parity with the Tables area).
  const { permissions: tablePerms } = useMyAppBatchPermissions(
    displayTables.map((t) => t.id),
  );

  // Handlers
  const handleToggleHidden = async (table: Form) => {
    if (!token) return;
    try {
      await myappApi.tables.update(token, table.id, { is_hidden: !table.is_hidden });
      refresh();
    } catch (err) {
      console.error("Failed to update table:", err);
    }
  };

  const handleCloneClick = (table: Form) => {
    setTableToClone(table);
    setCloneName(`${table.name} (Copy)`);
    setCloneDialogOpen(true);
  };

  const handleCloneConfirm = async () => {
    if (!tableToClone || !cloneName.trim() || !token) return;
    try {
      setCloning(true);
      await myappApi.tables.clone(token, tableToClone.id, cloneName);
      setCloneDialogOpen(false);
      setTableToClone(null);
      setCloneName("");
      refresh();
    } catch (err) {
      console.error("Failed to clone table:", err);
    } finally {
      setCloning(false);
    }
  };

  const handleDeleteClick = async (table: Form) => {
    setTableToDelete(table);
    setTableDeps(null);
    setDeleteError(null);
    setCheckingDeps(true);
    setDeleteDialogOpen(true);

    try {
      const deps = await myappApi.tables.checkDependencies(token, table.id);
      setTableDeps(deps);
    } catch (err) {
      console.error("Failed to check dependencies:", err);
      setDeleteError("Failed to check table dependencies. Please try again.");
    } finally {
      setCheckingDeps(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tableToDelete || !token) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await myappApi.tables.deleteWithConfirm(
        token,
        tableToDelete.id,
        tableToDelete.name,
        tableToDelete.is_published,
      );
      setDeleteDialogOpen(false);
      setTableToDelete(null);
      setTableDeps(null);
      refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete table";
      setDeleteError(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleTablesChanged = () => {
    refresh();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loadingApp) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading app...</div>
      </div>
    );
  }

  if (appError || !app) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="text-destructive">{appError || "App not found"}</div>
          <Link to="/portal/myapp/apps">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Apps
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/portal/myapp/apps"
          className="flex items-center gap-2 text-sm text-primary transition hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Apps
        </Link>

        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            {app.colour ? (
              <div
                className="w-6 h-6 rounded-full shrink-0 border"
                style={{ backgroundColor: app.colour }}
              />
            ) : (
              <Palette className="h-6 w-6 text-muted-foreground shrink-0" />
            )}
            <div className="min-w-0">
              <h1 className="text-2xl md:text-4xl font-bold text-slate-900">{app.name}</h1>
              {app.description && (
                <p className="mt-1 text-base md:text-lg text-slate-600">{app.description}</p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setEditTablesOpen(true)}
            className="rounded-full border-slate-200 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <Settings className="h-4 w-4 mr-2" />
            Edit Tables
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="rounded-2xl md:rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search tables..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <div className="flex items-center space-x-2">
            <Switch
              id="show-hidden"
              checked={showHidden}
              onCheckedChange={setShowHidden}
            />
            <Label htmlFor="show-hidden">Show hidden</Label>
          </div>
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {tablesError ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <p className="text-destructive">{tablesError}</p>
            <Button onClick={refresh} variant="outline">Try Again</Button>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayTables.length === 0 ? (
          <div className="text-center py-12">
            <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No tables match your search" : "No tables in this app"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Add tables to this app to get started."}
            </p>
            {!searchTerm && (
              <Button onClick={() => setEditTablesOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tables
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => requestSort("name")}
                  >
                    Name
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("table_type")}
                    onSort={() => requestSort("table_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("is_published")}
                    onSort={() => requestSort("is_published")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => requestSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayTables.map((table) => (
                  <TableRow
                    key={table.id}
                    onMouseEnter={() => prefetchTable(table.id)}
                  >
                    <TableCell>
                      <div>
                        <div className="flex items-center gap-2">
                          {(() => {
                            const tp = tablePerms[table.id];
                            // Published → View Data (any returned row is viewable);
                            // Draft → Edit Table (only navigable with edit rights).
                            const canOpen = table.is_published
                              ? tp?.can_view
                              : tp?.can_edit;
                            return canOpen ? (
                              <Link
                                to={
                                  table.is_published
                                    ? "/portal/myapp/$tableId"
                                    : "/portal/myapp/$tableId/edit"
                                }
                                params={{ tableId: String(table.id) }}
                                className="font-medium hover:underline text-primary"
                              >
                                {table.name}
                              </Link>
                            ) : (
                              <span className="font-medium">{table.name}</span>
                            );
                          })()}
                          {table.is_hidden && (
                            <Badge variant="outline" className="text-muted-foreground">
                              Hidden
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {table.table_name}
                          </code>
                          {table.description && (
                            <span className="text-xs text-muted-foreground truncate max-w-[300px]">
                              {table.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {TABLE_TYPE_LABELS[table.table_type] ?? table.table_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {table.is_published ? (
                        <Badge variant="info">Published</Badge>
                      ) : (
                        <Badge variant="secondary">Draft</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(table.created_at_utc)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Table actions">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {(() => {
                            const tp = tablePerms[table.id];
                            return table.is_published ? (
                              <>
                                {tp?.can_submit && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/submit"
                                      params={{ tableId: String(table.id) }}
                                      search={{}}
                                    >
                                      <ClipboardList className="h-4 w-4 mr-2" />
                                      Add Entry
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_view && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Table2 className="h-4 w-4 mr-2" />
                                      View Data
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canStructure && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/edit"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Settings className="h-4 w-4 mr-2" />
                                      View Structure
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canCreateTables && (
                                  <DropdownMenuItem onClick={() => handleCloneClick(table)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Clone
                                  </DropdownMenuItem>
                                )}
                                {tp?.is_owner && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/permissions"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <ShieldCheck className="h-4 w-4 mr-2" />
                                      Permissions
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canHide && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleHidden(table)}>
                                      {table.is_hidden ? (
                                        <>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Show
                                        </>
                                      ) : (
                                        <>
                                          <EyeOff className="h-4 w-4 mr-2" />
                                          Hide
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_delete_table && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(table)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </>
                            ) : (
                              <>
                                {tp?.can_edit && canStructure && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/edit"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <Pencil className="h-4 w-4 mr-2" />
                                      Edit Table
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canCreateTables && (
                                  <DropdownMenuItem onClick={() => handleCloneClick(table)}>
                                    <Copy className="h-4 w-4 mr-2" />
                                    Clone
                                  </DropdownMenuItem>
                                )}
                                {tp?.is_owner && (
                                  <DropdownMenuItem asChild>
                                    <Link
                                      to="/portal/myapp/$tableId/permissions"
                                      params={{ tableId: String(table.id) }}
                                    >
                                      <ShieldCheck className="h-4 w-4 mr-2" />
                                      Permissions
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {tp?.can_edit && canHide && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleToggleHidden(table)}>
                                      {table.is_hidden ? (
                                        <>
                                          <Eye className="h-4 w-4 mr-2" />
                                          Show
                                        </>
                                      ) : (
                                        <>
                                          <EyeOff className="h-4 w-4 mr-2" />
                                          Hide
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {tp?.can_delete_table && (
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(table)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                )}
                              </>
                            );
                          })()}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Tables Dialog */}
      {app && (
        <EditTablesDialog
          appId={app.id}
          open={editTablesOpen}
          onOpenChange={setEditTablesOpen}
          onTablesChanged={handleTablesChanged}
        />
      )}

      {/* Clone Table Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clone Table</DialogTitle>
            <DialogDescription>
              Create a copy of &quot;{tableToClone?.name}&quot; with all its
              fields. The new table will be a draft that you can modify before
              publishing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="clone-name">New Table Name</Label>
              <Input
                id="clone-name"
                placeholder="e.g., Customer (Copy)"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && cloneName.trim()) {
                    handleCloneConfirm();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCloneDialogOpen(false)}
              disabled={cloning}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneConfirm}
              disabled={cloning || !cloneName.trim()}
            >
              <Copy className="h-4 w-4 mr-2" />
              {cloning ? "Cloning..." : "Clone Table"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Table Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Table</DialogTitle>
            <DialogDescription>
              {checkingDeps
                ? "Checking for dependencies..."
                : tableDeps && !tableDeps.can_delete
                  ? `"${tableToDelete?.name}" cannot be deleted because it is referenced by other tables.`
                  : `Are you sure you want to delete "${tableToDelete?.name}"? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>

          {checkingDeps && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!checkingDeps && tableDeps && !tableDeps.can_delete && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
              <p className="font-medium text-destructive mb-2">Referenced by:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                {tableDeps.dependencies.map((dep, i) => (
                  <li key={i}>
                    <span className="font-medium">{dep.table_name}</span>
                    {" "}&rarr; {dep.field_name}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">
                Remove these references first, then try again.
              </p>
            </div>
          )}

          {!checkingDeps && tableDeps?.can_delete && tableToDelete?.is_published && (
            <div className="rounded-md border border-orange-500/50 bg-orange-500/5 p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-orange-700 dark:text-orange-400">
                    This table is published
                    {tableDeps.record_count != null && tableDeps.record_count > 0
                      ? ` and contains ${tableDeps.record_count.toLocaleString()} record${tableDeps.record_count === 1 ? "" : "s"}`
                      : ""}
                    .
                  </p>
                  <p className="text-muted-foreground mt-1">
                    The physical database table and all its data will be permanently deleted.
                  </p>
                </div>
              </div>
            </div>
          )}

          {deleteError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              {deleteError}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting || checkingDeps || (tableDeps != null && !tableDeps.can_delete)}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
